'use client';

import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';

import { LOCAL } from '@/lib/environments';

const VERSION_CHECK_ENDPOINT = `${process.env.NEXT_PUBLIC_BASE_URL}_next/static/version.json`;
const CHECK_INTERVAL = 5 * 60 * 1000; // Check every 5 minutes

interface VersionResponse {
    version: string;
}

export function useAppVersionCheck() {
    const pathname = usePathname();
    const refreshOnReloadRef = useRef(false);

    const handleRouteChange = () => {
        if (refreshOnReloadRef.current) {
            console.warn('[APP Version Check]: Reloading for new version...');
            window.location.reload();
        }
    };

    useEffect(() => {
        // Skip version check in local development
        if (process.env.NEXT_PUBLIC_ENV === LOCAL) {
            return;
        }

        const checkVersion = async () => {
            try {
                const response = await fetch(VERSION_CHECK_ENDPOINT, {
                    cache: 'no-store',
                    headers: {
                        'Cache-Control': 'no-cache'
                    }
                });

                // Only parse JSON when the response is successful (status 200-299)
                if (!response?.ok) return;

                const data: VersionResponse = await response.json();
                const latestVersion = data?.version;

                // Guard against missing version to prevent storing "undefined"
                if (!latestVersion) return;

                const localVersion = localStorage.getItem('appVersion');

                // First visit: store version without triggering reload
                if (!localVersion) {
                    localStorage.setItem('appVersion', latestVersion);
                    return;
                }

                if (latestVersion !== localVersion) {
                    localStorage.setItem('appVersion', latestVersion);
                    refreshOnReloadRef.current = true;
                    return;
                }

                refreshOnReloadRef.current = false;
            } catch {
                // Silently fail - don't disrupt user experience
            }
        };

        // Check version immediately on mount
        checkVersion();

        // Set up interval to check every 5 minutes
        const intervalToCheckVersion = setInterval(checkVersion, CHECK_INTERVAL);

        return () => {
            if (intervalToCheckVersion) {
                clearInterval(intervalToCheckVersion);
            }
        };
    }, []);

    // Listen for route changes to trigger reload if needed
    useEffect(() => {
        handleRouteChange();
    }, [pathname]);
}
